import { ArrayFilterFn, DictionaryFilterFn, dictionaryToArray} from './internal'

/**
 * Simple array filter (wraps native Array#filter)
 */
export function _filter<T, R extends T = T>(collection: Array<T>, filterFn: ArrayFilterFn<T, R>): R[];

/**
 * Object pick filter - pick the given key from each object in the given array and filter based on that being === true
 */
export function _filter<T, K extends keyof T>(collection: Array<T>, filterFn: K): T[];

/**
 * Object matcher filter - filter to objects in the given array where each of the corresponding properties is
 * strictly equal (===) to the supplied matcher.
 */
export function _filter<T>(collection: T[], objectMatcher: Partial<T>): T[];

/**
 *  Simple object filter (takes each Key-Value from the given object and filters based on the value) - Returns an array
 */
export function _filter<T extends object, R extends T[keyof T]>(collection: T, filterFn: DictionaryFilterFn<T, R>): R[];

/**
 * Object pick filter for lookup objects - pick the given key from each object and filter based on that being === true
 * (Objects that are are found at each key of the supplied 'collection' object.)
 */
export function _filter<T extends object, K extends keyof T[keyof T]>(collection: T, filterFn: K): Array<(T[keyof T])>;

/**
 * Object matcher filter - filter to objects in the given dictionary where each of the corresponding properties is
 * strictly equal (===) to the supplied matcher.
 */
export function _filter<T extends object>(collection: T, objectMatcher: Partial<T[keyof T]>): Array<T[keyof T]>;

/**
 * Null | undefined will return an empty array
 */
export function _filter<T, R>(collection: undefined | null, filterFn: any): R[];

/**
 * Override implementation.
 */
export function _filter<T, R extends T>(collection: any, predicate: any): R[] {
	if (!collection || !predicate || typeof collection !== 'object') return []

	if (typeof predicate === 'string') {
		predicate = { [predicate]: true }
	}

	if (Array.isArray(collection)) {
		if (typeof predicate === 'function') {
			return collection.filter(predicate)
		}
		return iterativeFilter(collection, predicate)
	}

	const list = dictionaryToArray(collection)

	if (typeof predicate === 'function') {
		// Performance hit here for wrapping the predicate fn in another anonymous function - we need to pass key.
		return list.filter((item) => predicate(item.value, item.key, collection)).map(item => item.value)
	}

	// Performance hit here for extra Array#map - don't expect callers to iterate over dictionaries with 10k+ entries.
	return iterativeFilter(list.map(item => item.value), predicate)
}

function iterativeFilter(collection: any[], predicateObject: any): any {
	const predicateKeys = Object.keys(predicateObject)
	const result = []
	for (let ii = 0; ii < collection.length; ii++) {
		const item = collection[ii]
		let match = true
		for (const key of predicateKeys) {
			if (item[key] !== predicateObject[key]) {
				match = false
				break
			}
		}
		if (match) result.push(item)
	}
	return result
}